import React, { useMemo } from 'react';
import { Bar, BarChart, Cell, YAxis } from 'recharts';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';

interface MiniBarChartProps {
  // One signed value per day. Positive renders above the zero baseline (green),
  // negative below (red) — a compact diverging bar sparkline.
  values: number[];
  width?: number;
  height?: number;
}

const DEFAULT_WIDTH = 72;
const DEFAULT_HEIGHT = 28;

const MiniBarChartComponent: React.FC<MiniBarChartProps> = ({
  values,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}) => {
  const tokens = useChartTokens();

  const data = useMemo(() => values.map((value) => ({ value })), [values]);
  const domain = useMemo<[number, number]>(() => {
    const max = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0);
    return max === 0 ? [-1, 1] : [-max, max];
  }, [values]);

  return (
    <BarChart
      width={width}
      height={height}
      data={data}
      margin={{ top: 1, right: 0, bottom: 1, left: 0 }}
      barCategoryGap={1}
    >
      {/* Symmetric domain keeps the zero baseline centered. */}
      <YAxis hide domain={domain} />
      <Bar dataKey="value" isAnimationActive={false} radius={1}>
        {data.map((entry, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Cell key={index} fill={entry.value >= 0 ? tokens.pos : tokens.neg} />
        ))}
      </Bar>
    </BarChart>
  );
};

export const MiniBarChart = TypedMemo(MiniBarChartComponent);
